import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectRouteAlertReport,
  setRouteAlertReport,
} from '../../utility/Redux/list';
import { selectToken } from '../../utility/Redux/security';
import {
  selectPrefix,
  selectStationId,
  selectStationName,
  selectUserType,
  setStationID,
  setStationName,
} from '../../utility/Redux/profile';
import {
  columns,
  columnsMobileView,
} from '../../utility/columns/alertReportCol';
import {
  setCollectionAccountDetails,
  setListStationMasterModalStatus,
  setPartyLedgerModalStatus,
  setviewBillModalStatus,
} from '../../utility/Redux/modal';
import ViewBillModal from '../../utility/component/modals/modBillInvoice';
import { selectStatus } from '../../utility/Redux/modal';
import ModListStationMaster from '../../utility/component/modals/modListStationMaster';
import { FromToDate } from '../../utility/component/fromToDate';
import { getRouteAlertReport } from '../../utility/list/routeAlertReportList';
import ReactToPrint from 'react-to-print';
import GeniusTableForReports from '../../utility/component/geniusComponents/geniusTableforReports';
import IsLocalData from '../../utility/functions/ceckLocalData';
import ReportDataTable from '../../utility/component/datagrid/ReportDataTable';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  FileText,
  MapPin,
  Printer,
  Search,
  X,
} from 'lucide-react';

const RouteAlert = () => {
  const userType = useSelector(selectUserType);
  const ReduxStationName = useSelector(selectStationName);
  const ReduxStation_id = useSelector(selectStationId);
  const printRef = useRef();
  const token = useSelector(selectToken);
  const prefix = useSelector(selectPrefix);
  const dispatch = useDispatch();
  const routeAlertList = useSelector(selectRouteAlertReport);
  const loading = useSelector(selectStatus);
  const [searchTerm, setSearchTerm] = useState('');

  const reportRows = useMemo(
    () => (Array.isArray(routeAlertList) ? routeAlertList : []),
    [routeAlertList]
  );

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return reportRows;

    return reportRows.filter((row) =>
      [row.ACCOUNT_NO, row.NAME, row.ADDRESS, row.MOBILE, row.SNAME, row.SID]
        .filter((value) => value !== null && value !== undefined)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [reportRows, searchTerm]);

  const summary = useMemo(() => {
    const days = reportRows
      .map((row) => Number(row.NO_OF_DAYS))
      .filter((value) => Number.isFinite(value));

    return {
      totalAlerts: reportRows.length,
      stations: new Set(
        reportRows
          .map((row) => row.SID || row.SNAME)
          .filter((value) => value !== null && value !== undefined && value !== '')
      ).size,
      maxDays: days.length ? Math.max(...days) : 0,
    };
  }, [reportRows]);

  const okButton = () => {
    getRouteAlertReport(
      dispatch,
      prefix,
      token,
      ReduxStation_id,
      ReduxStationName
    );

    dispatch(setviewBillModalStatus(false));
  };

  useEffect(() => {
    const callback = (details, data) => {
      dispatch(setRouteAlertReport(data));
      dispatch(setStationID(details[0].SID));
      dispatch(setStationName(details[0].SNAME));
    };

    const elseFunc = () => {
      if (userType !== 'USER') {
        dispatch(setStationID(''));
        dispatch(setStationName(''));
      }
    };

    IsLocalData('Route_Alert_Report', callback, elseFunc);
  }, [dispatch, userType]);

  const StatCard = ({ icon: Icon, label, value, tone }) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>
          <Icon size={19} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {label}
          </p>
          <p className="mt-0.5 truncate text-xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="mx-auto flex min-h-full w-full max-w-[1800px] flex-col gap-4 p-3 sm:p-5">
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <AlertTriangle size={22} />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-slate-900">
                  Route Alert Report
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Review inactive parties and route exceptions that need attention.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <MapPin size={16} className="text-slate-400" />
              <span className="text-slate-500">Station:</span>
              <span className="max-w-[220px] truncate font-semibold text-slate-800">
                {ReduxStationName || ReduxStation_id || 'All stations'}
              </span>
            </div>
          </div>

          <div className="p-3 sm:p-4">
          <FromToDate
            submit={okButton}
            reset={() => {
              if (userType === 'USER') {
                dispatch(
                  setCollectionAccountDetails({
                    accName: '',
                    accAddress: '',
                    accMobile: '',
                    accNumber: '',
                    stationId: '',
                  })
                );
              } else {
                dispatch(setStationID(''));
                dispatch(setStationName(''));
                dispatch(
                  setCollectionAccountDetails({
                    accName: '',
                    accAddress: '',
                    accMobile: '',
                    accNumber: '',
                    stationId: '',
                  })
                );
              }
            }}
            date={{
              status: false,
            }}
            partyName={false}
          />
          </div>
        </section>

        <ModListStationMaster
          onRowClick={(p) => {
            dispatch(setStationID(p.row.CODE));
            dispatch(setStationName(p.row.STATION_NAME));
            dispatch(setListStationMasterModalStatus(false));
          }}
        />
        <ViewBillModal />

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard
            icon={FileText}
            label="Total alerts"
            value={summary.totalAlerts.toLocaleString()}
            tone="bg-rose-50 text-rose-600"
          />
          <StatCard
            icon={Building2}
            label="Stations covered"
            value={summary.stations.toLocaleString()}
            tone="bg-blue-50 text-blue-600"
          />
          <StatCard
            icon={CalendarClock}
            label="Highest inactive days"
            value={summary.maxDays ? `${summary.maxDays} days` : '—'}
            tone="bg-amber-50 text-amber-700"
          />
        </section>

        <section className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">Alert details</h2>
              <p className="text-xs text-slate-500">
                Double-click a row to open the party ledger.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative">
                <Search
                  size={17}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search party, mobile or station"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-9 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:w-72"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {reportRows.length > 0 && (
                <ReactToPrint
                  trigger={() => (
                    <button className="flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-700">
                      <Printer size={16} />
                      Print report
                    </button>
                  )}
                  content={() => printRef.current}
                />
              )}
            </div>
          </div>

          <ReportDataTable
            columns={window.innerWidth < 770 ? columnsMobileView : columns}
            rows={filteredRows}
            loading={loading}
            getRowKey={(row) => row.ACCOUNT_NO}
            emptyTitle={searchTerm ? 'No matching alerts' : 'No route alerts found'}
            emptyDescription={
              searchTerm
                ? 'Try another party name, account number, mobile or station.'
                : 'Select a station and run the report to view route alerts.'
            }
            onRowDoubleClick={(row) => {
              dispatch(setPartyLedgerModalStatus(true));
              dispatch(
                setCollectionAccountDetails({
                  accName: row.NAME,
                  accAddress: row.ADDRESS,
                  accMobile: row.MOBILE,
                  accNumber: row.ACCOUNT_NO,
                  stationId: row.SID,
                })
              );
            }}
          />
        </section>

        <div
          ref={printRef}
          className="hidden print:block"
        >
          <GeniusTableForReports
            stationId={ReduxStation_id}
            columns={columns}
            data={routeAlertList}
            title={'Route Alert Reports'}
          />
        </div>
      </div>
    </div>
  );
};

export default RouteAlert;
